import styled from "@emotion/styled";
import t from "../../../helper/t";
import MainCard from "../elements/MainCard";
import IndexCardSlider from "../../../components/layout/index/IndexCardSlider";

import imageIndexMeasurementExampleSvg from "../../../../assets/images/common/index-measurement-example.svg";
import imageMessung1 from "../../../../public/img/know_how/messung_1.png";
import imageMessung2 from "../../../../public/img/know_how/messung_2.png";
import CommonContainer from "../elements/CommonContainer";

const ContentPartDiv = styled.div(({ theme }) => ([
  {
    display: "flex",
    flexDirection: "column",
    rowGap: 56,

    marginTop: 40,
    marginBottom: 78,
    marginLeft: "8.33vw",
    marginRight: "8.33vw",

    /* for mobile */
    [theme.splus3.breakpoints.mobile]: {
      rowGap: 32,
      marginTop: 8,
      marginLeft: 16,
      marginRight: 16,
    },
  }
]));

const TextH2Div = styled.div(({ theme }) => ([
  props => props.theme.splus3.foreground.primary,
  props => props.theme.splus3.typography.h2Bold,
  {
    /* for mobile */
    [theme.splus3.breakpoints.mobile]: [
      theme.splus3.foreground.monochrome500,
      theme.splus3.typography.h1m,
    ],
  }
]));

const TextH3Div = styled.div(({ theme }) => ([
  props => props.theme.splus3.foreground.primary,
  props => props.theme.splus3.typography.h3,
  {
    /* for mobile */
    [theme.splus3.breakpoints.mobile]: [
      theme.splus3.foreground.monochrome500,
      theme.splus3.typography.h2m,
    ],
  }
]));

const BodyTextDiv = styled.div(({ theme }) => ([
  props => props.theme.splus3.foreground.primary,
  props => props.theme.splus3.typography.default,
  {
    /* for mobile */
    [theme.splus3.breakpoints.mobile]: [
      theme.splus3.foreground.monochrome500,
      theme.splus3.typography.small,
    ],
  }
]));

const ImageContainerDesktop = styled(MainCard)(({ theme }) => ([
  {
    alignSelf: "center",
    maxWidth: 720,
    padding: 20,

    /* for mobile */
    [theme.splus3.breakpoints.mobile]: [
      {
        display: "none",
      }
    ],
  }
]));

const KnowHowBlockContainer = styled.div(({ theme }) => ({
  display: "grid",
  gridTemplateRows: "repeat(1, 1fr)",
  gridTemplateColumns: "repeat(3, 1fr)",

  gap: 30,

  [theme.splus3.breakpoints.mobile]: {
    gridTemplateRows: "repeat(3, auto)",
    gridTemplateColumns: "repeat(1, 1fr)",
  },
}));

const KnowHowBlock = styled(BodyTextDiv)(({ theme }) => [
  {
    display: "flex",
    flexDirection: "column",
    rowGap: 12,
  }
]);

const KnowHowBlockHeader = styled.div(({ theme }) => [
  theme.splus3.typography.defaultBold,
]);

const ImageContainerMobile = styled.div(({ theme }) => ([
  theme.splus3.background.white,
  {
    padding: "10px",
    boxSizing: "border-box",

    width: "290px",
    display: "none",
    gridTemplateRows: "repeat(2, auto)",
    gridTemplateColumns: "repeat(3, 1fr)",

    borderRadius: "6px",
    boxShadow: "0 4px 10px 0 rgba(0, 44, 119, 0.18)",
    zIndex: 100, // we want the box shadow to display over the element underneath

    img: {
      width: "100%",
      height: "auto",
      gridColumn: "1 / span 3",
    },

    span: [
      theme.splus3.typography.font12,
      {
        boxSizing: "border-box",
        padding: "0 2px",
      },
    ],

    "span:nth-child(2)": {
      textAlign: "left",
    },
    "span:nth-child(3)": {
      textAlign: "center",
    },
    "span:nth-child(4)": {
      textAlign: "right",
    },

    [theme.splus3.breakpoints.mobile]: {
      display: "grid",
    },
  }
]));


interface InfoPageProps {
}

const InfoPage: React.FC<InfoPageProps> = () => {
  return (
    <CommonContainer>
      <ContentPartDiv>
        <TextH2Div>
          {t("Was macht der Speedtest Plus?")}
        </TextH2Div>
        <BodyTextDiv>
          {t("Du möchtest wissen, wie schnell Dein Internet wirklich ist? Der Speedtest Plus misst die Geschwindigkeiten von Upload, Download und der Reaktionszeit -Ping- in Deinem Heimnetzwerk.")}
          <br />
          <br />
          {t("Die tatsächliche Geschwindigkeit hängt von vielen verschiedenen Faktoren ab. Wenn wir beim Test merken, dass Dein Internet langsamer ist als der Tarif verspricht, prüfen wir bei Vodafone-Kunden mit Kabel-Anschluss noch die Strecke von unseren Servern bis zu Deinem Router. Und untersuchen Dein Heimnetzwerk. Keine Sorge, persönliche Daten werden dabei nicht erhoben.")}
        </BodyTextDiv>
        <ImageContainerDesktop><img width="100%" src={imageIndexMeasurementExampleSvg.src} /></ImageContainerDesktop>
        <TextH3Div>
          {t("Die tatsächliche Geschwindigkeit hängt von vielen Faktoren ab")}
        </TextH3Div>
        <KnowHowBlockContainer>
          <KnowHowBlock>
            <KnowHowBlockHeader>{t("Messung 1")}</KnowHowBlockHeader>
            {t("Im ersten Schritt prüfen wir die Download- und Upload-Geschwindigkeit sowie die Reaktionszeit Deines Computers.")}
            <ImageContainerMobile>
              <img src={imageMessung1.src} alt="messung1" />
              <span>{t("Unser Server")}</span>
              <span>{t("Router")}</span>
              <span>{t("Dein Smartphone")}</span>
            </ImageContainerMobile>
          </KnowHowBlock>
          <KnowHowBlock>
            <KnowHowBlockHeader>{t("Messung 2")}</KnowHowBlockHeader>
            {t("Wenn wir bei der ersten Messung merken, dass Dein Internet langsamer ist als gedacht, prüfen wir bei Vodafone-Kunden mit Kabel-Anschluss noch die Strecke von unseren Servern bis zu Deinem Router.")}
            <ImageContainerMobile>
              <img src={imageMessung2.src} alt="messung2" />
              <span>{t("Unser Server")}</span>
              <span>{t("Router")}</span>
            </ImageContainerMobile>
          </KnowHowBlock>
          <KnowHowBlock>
            <KnowHowBlockHeader>{t("Heimnetzwerk-Analyse")}</KnowHowBlockHeader>
            {t("Wenn nötig, analysieren wir noch Deinen Software- und Gerätestatus, um Dir individuelle Optimierungs-Tipps zu geben.")}
          </KnowHowBlock>
        </KnowHowBlockContainer>
        <TextH3Div>
          {t("Beste Voraussetzungen für exakte Ergebnisse")}
        </TextH3Div>
        <IndexCardSlider />
      </ContentPartDiv>
    </CommonContainer>
  );
}

export default InfoPage;
